import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Mail, Tag, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { LiveClock } from "@/components/LiveClock";
import { toast } from "sonner";

// 🆕 Feb 2026 — Costruisce una label leggibile per il device (compare in "Impostazioni sicurezza")
function buildDeviceLabel() {
  try {
    const ua = navigator.userAgent || "";
    const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
    const browser = m ? m[1] : "Browser";
    let os = "";
    if (/Windows/.test(ua)) os = "Windows";
    else if (/Macintosh|Mac OS X/.test(ua)) os = "Mac";
    else if (/Linux/.test(ua)) os = "Linux";
    else if (/Android/.test(ua)) os = "Android";
    else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
    else os = "Sconosciuto";
    return `${browser} su ${os}`;
  } catch { return "Dispositivo"; }
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [landingActive, setLandingActive] = useState(false);
  // 🆕 Feb 2026 — Stato OTP challenge
  const [otpChallenge, setOtpChallenge] = useState(null); // { challenge_id, email_masked, expires_in_seconds }
  const [otpCode, setOtpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const { login, verifyOtp } = useAuth();
  const navigate = useNavigate();

  // Verifica se la landing pubblica è attiva (per mostrare link "Vedi i piani")
  useEffect(() => {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/landing`)
      .then(() => setLandingActive(true))
      .catch(() => setLandingActive(false));
  }, []);

  const sendForgot = async () => {
    if (!forgotEmail) return;
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Se l'email esiste, riceverai un link di reset entro pochi minuti.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setForgotSending(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await login(email, password);
      // 🆕 Feb 2026 — Se il backend richiede OTP, apri la seconda schermata di verifica.
      if (data?.needs_otp) {
        setOtpChallenge({
          challenge_id: data.challenge_id,
          email_masked: data.email_masked,
          expires_in_seconds: data.expires_in_seconds,
        });
        return;
      }
      if (data?.role === "cliente") navigate("/area-riservata");
      else if (data?.role === "broker") navigate("/area-broker");
      else if (data?.role === "company") navigate("/area-compagnia");
      else if (data?.role === "super_admin") navigate("/superadmin");
      else navigate("/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Feb 2026 — Verifica il codice OTP e completa il login
  const submitOtp = async (e) => {
    e?.preventDefault();
    setErr("");
    setOtpVerifying(true);
    try {
      const data = await verifyOtp({
        challenge_id: otpChallenge.challenge_id,
        otp: (otpCode || "").trim(),
        remember_device: rememberDevice,
        device_label: buildDeviceLabel(),
      });
      if (data?.role === "cliente") navigate("/area-riservata");
      else if (data?.role === "broker") navigate("/area-broker");
      else if (data?.role === "company") navigate("/area-compagnia");
      else if (data?.role === "super_admin") navigate("/superadmin");
      else navigate("/dashboard");
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setOtpVerifying(false);
    }
  };

  const cancelOtp = () => {
    setOtpChallenge(null);
    setOtpCode("");
    setRememberDevice(false);
    setErr("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/18055564/pexels-photo-18055564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A192F]/85" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img
              src="/gestagepro-logo.png"
              alt="GestAgePro"
              className="h-14 w-auto object-contain drop-shadow-lg bg-white/95 rounded-md p-1.5"
              data-testid="brand-logo-left"
            />
            <div>
              <div className="font-display font-semibold text-lg tracking-tight leading-none">GestAgePro</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300 mt-1">Gestionale Agenzie Professionale</div>
            </div>
          </div>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-4">Insurance OS</p>
            <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-tight">
              La piattaforma per la tua agenzia indipendente.
            </h1>
            <p className="mt-6 text-slate-300 leading-relaxed">
              CRM, polizze, preventivi con AI e portale cliente. Tutto sotto un unico tetto.
            </p>
            <div className="mt-10 flex items-center gap-2 text-sm text-slate-300">
              <ShieldCheck className="h-4 w-4 text-blue-300" />
              <span>RBAC con tre ruoli · auditing · backup quotidiani</span>
            </div>
            <ContactForm />
          </div>
          <div className="text-xs text-slate-400">© {new Date().getFullYear()} GestAgePro</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md animate-fade-up">
          {/* Logo GestAgePro (visibile su tutti i breakpoint) */}
          <div className="flex flex-col items-center mb-6" data-testid="brand-logo-right-wrap">
            <img
              src="/gestagepro-logo.png"
              alt="GestAgePro — Gestionale Agenzie Professionale"
              className="h-24 sm:h-28 w-auto object-contain"
              data-testid="brand-logo-right"
            />
          </div>
          <div className="mb-10">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-600">Accesso CRM</p>
              <span className="text-slate-500">
                <LiveClock inline />
              </span>
            </div>
            <h2 className="font-display text-3xl font-semibold tracking-tight">
              {otpChallenge ? "Verifica accesso" : "Bentornato."}
            </h2>
            <p className="text-slate-500 mt-2">
              {otpChallenge
                ? <>Abbiamo inviato un codice a <strong>{otpChallenge.email_masked}</strong>. Inseriscilo qui sotto per completare l&apos;accesso.</>
                : "Accedi al pannello agenzia."}
            </p>
          </div>

          {otpChallenge ? (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Codice di verifica (6 cifre)</Label>
                <Input
                  data-testid="login-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  className="mt-1.5 h-12 text-2xl text-center tracking-[0.6em] font-mono"
                  placeholder="••••••"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Il codice è valido per {Math.round((otpChallenge.expires_in_seconds || 300) / 60)} minuti.
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none bg-blue-50/60 border border-blue-200 rounded-md p-2.5">
                <input
                  type="checkbox"
                  data-testid="login-remember-device"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-600"
                />
                <span>
                  <strong>Ricorda questo dispositivo per 30 giorni</strong>
                  <span className="block text-[11px] text-slate-500 mt-0.5">
                    Su questo dispositivo non ti verrà più chiesto l&apos;OTP fino alla scadenza o alla revoca dal tuo profilo.
                  </span>
                </span>
              </label>
              {err && (
                <div data-testid="login-error" className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">
                  {err}
                </div>
              )}
              <Button
                type="submit"
                data-testid="login-otp-submit"
                disabled={otpVerifying || otpCode.length !== 6}
                className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white"
              >
                {otpVerifying ? "Verifica…" : (
                  <span className="inline-flex items-center gap-2"><KeyRound className="h-4 w-4" /> Verifica e accedi</span>
                )}
              </Button>
              <button
                type="button"
                onClick={cancelOtp}
                data-testid="login-otp-cancel"
                className="w-full text-center text-sm text-slate-600 hover:text-slate-800 mt-2"
              >
                ← Annulla e cambia account
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@dominio.it"
                required
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 h-11"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white"
            >
              {loading ? "Accesso in corso…" : (
                <span className="inline-flex items-center gap-2">Accedi <ArrowRight className="h-4 w-4" /></span>
              )}
            </Button>

            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-800 mt-2"
                >
                  Password dimenticata?
                </button>
              </DialogTrigger>              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-blue-600" /> Recupera password</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Inserisci la tua email. Se l&apos;account esiste riceverai un link di reset.</p>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="mt-1.5" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForgotOpen(false)}>Annulla</Button>
                  <Button data-testid="forgot-submit" disabled={forgotSending || !forgotEmail} onClick={sendForgot} className="bg-[#0F172A] hover:bg-[#1E293B]">
                    {forgotSending ? "Invio…" : "Invia link"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {landingActive && (
              <p className="text-center text-xs text-slate-500 mt-3">
                <a href="/landing" data-testid="login-see-plans" className="text-blue-600 hover:underline font-medium inline-flex items-center gap-0.5"><Tag className="h-3 w-3" /> Vedi i piani</a>
              </p>
            )}
          </form>
          )}

          <div className="mt-6 text-center bg-blue-50/60 border border-blue-100 rounded-md p-3">
            <p className="text-xs text-slate-600 mb-1">Non hai un account?</p>
            <a
              href="/richiedi-preventivo"
              data-testid="public-quote-link"
              className="text-sm font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
            >
              Richiedi un preventivo online →
            </a>
          </div>

          <p className="mt-8 text-center text-[11px] text-slate-400" data-testid="credits">
            Software realizzato by <span className="font-semibold text-slate-600">Vincenzo Amoroso</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// 🆕 Feb 2026 — Form Contattaci nella landing pubblica (sotto la riga RBAC)
function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Il nome è obbligatorio.");
    if (!isValidEmail(form.email)) return setErr("Inserisci un'email valida.");
    if (!form.message.trim()) return setErr("Il messaggio è obbligatorio.");
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const { data } = await axios.post(`${apiBase}/public/contact-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        website: form.website, // honeypot — deve essere vuoto per gli umani
      });
      if (data?.ok) {
        setOk(true);
        setForm({ name: "", email: "", phone: "", message: "", website: "" });
        toast.success("Richiesta inviata! Ti contatteremo a breve.");
      }
    } catch (e2) {
      if (e2?.response?.status === 429) {
        setErr("Troppe richieste dallo stesso indirizzo. Riprova tra un'ora.");
      } else {
        setErr(e2?.response?.data?.detail || "Errore invio. Riprova tra poco.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <div className="mt-6 bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-4" data-testid="contact-form-success">
        <p className="text-sm text-emerald-200 font-medium">✓ Richiesta ricevuta</p>
        <p className="text-xs text-slate-300 mt-1">Grazie! Ti risponderemo il prima possibile all&apos;indirizzo email fornito.</p>
        <button
          type="button"
          onClick={() => setOk(false)}
          className="mt-3 text-[11px] text-blue-300 hover:text-blue-200 underline"
          data-testid="contact-form-new"
        >
          Invia un&apos;altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-2.5" data-testid="contact-form">
      <p className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2">Contattaci</p>
      {/* 🆕 Honeypot: nascosto agli umani, i bot lo compilano → richiesta scartata lato server */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="cf-website">Il tuo sito web (lascia vuoto)</label>
        <input
          id="cf-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Input
          data-testid="contact-name"
          type="text"
          placeholder="Nome*"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="h-9 bg-white/10 border-white/20 text-white placeholder:text-slate-400 text-sm"
          required
          maxLength={100}
        />
        <Input
          data-testid="contact-email"
          type="email"
          placeholder="Email*"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="h-9 bg-white/10 border-white/20 text-white placeholder:text-slate-400 text-sm"
          required
          maxLength={200}
        />
      </div>
      <Input
        data-testid="contact-phone"
        type="tel"
        placeholder="Telefono (opzionale)"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        className="h-9 bg-white/10 border-white/20 text-white placeholder:text-slate-400 text-sm"
        maxLength={40}
      />
      <textarea
        data-testid="contact-message"
        placeholder="Messaggio*"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        rows={3}
        maxLength={5000}
        required
        className="w-full rounded-md bg-white/10 border border-white/20 text-white placeholder:text-slate-400 text-sm px-3 py-2 resize-y min-h-[68px]"
      />
      {err && (
        <p className="text-[11px] text-rose-300" data-testid="contact-form-error">{err}</p>
      )}
      <Button
        type="submit"
        data-testid="contact-submit"
        disabled={busy}
        className="w-full h-9 bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold"
      >
        {busy ? "Invio in corso..." : "Invia richiesta"}
      </Button>
    </form>
  );
}

